/* eslint-disable react/no-unescaped-entities */
import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ShieldCheck, 
  Scales, 
  FileText, 
  Warning, 
  Gavel,
  ArrowLeft,
  EnvelopeSimple,
  CheckCircle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

const COMPANY = "Ganso Gordo Producciones LLC";
const PLATFORM = "LustLink";
const SUPPORT_EMAIL = "support@lust-link.net";
const LEGAL_EMAIL = "legal@lust-link.net";
const BILLING_EMAIL = "billing@lust-link.net";
const DMCA_EMAIL = "dmca@lust-link.net";
const ABUSE_EMAIL = "abuse@lust-link.net";
const SITE_URL = "https://lust-link.net";
const COMPANY_ADDRESS_LINE1 = "732 S 6th St Ste R";
const COMPANY_ADDRESS_LINE2 = "Las Vegas, NV 89101";
const COMPANY_ADDRESS_COUNTRY = "United States";
const GOVERNING_STATE = "Nevada";

// ==========================================
// Terms of Service
// ==========================================
const TermsOfService = () => (
  <div className="prose-custom">
    <h1>Terms of Service</h1>
    <p className="text-slate-400 text-sm">Last updated: June 2026 · Effective immediately for new users, and 30 days from posting for existing users.</p>

    <h2>1. About This Agreement</h2>
    <p>These Terms of Service (the <strong>"Terms"</strong>) form a binding legal agreement between you (<strong>"you"</strong>, <strong>"User"</strong>, or, if you upload content, <strong>"Creator"</strong>) and <strong>{COMPANY}</strong>, a Nevada limited liability company with its records-keeping and registered agent address at {COMPANY_ADDRESS_LINE1}, {COMPANY_ADDRESS_LINE2}, {COMPANY_ADDRESS_COUNTRY} (<strong>"{COMPANY}"</strong>, <strong>"we"</strong>, <strong>"us"</strong>, or <strong>"our"</strong>). {COMPANY} operates several divisions and brands, including the website {PLATFORM} located at <a href={SITE_URL} className="text-blue-400">{SITE_URL}</a> (collectively, the <strong>"Platform"</strong>).</p>
    <p>By creating an account, accessing the Platform, or using any of its features, you confirm that you have read, understood, and agreed to these Terms, our <Link to="/legal/privacy" className="text-blue-400">Privacy Policy</Link>, our <Link to="/legal/refund" className="text-blue-400">Refund Policy</Link>, our <Link to="/legal/content-policy" className="text-blue-400">Content Policy</Link>, our <Link to="/legal/2257" className="text-blue-400">18 U.S.C. § 2257 Compliance Statement</Link>, and, if you upload content, our <Link to="/legal/creator-agreement" className="text-blue-400">Creator Agreement</Link>. <strong>If you do not agree, you must not use the Platform.</strong></p>

    <h2>2. Eligibility &amp; Age Requirement</h2>
    <p>The Platform contains sexually explicit material intended only for adults. By registering or accessing the Platform you affirmatively represent and warrant that:</p>
    <ul>
      <li>You are at least <strong>eighteen (18) years of age</strong> (or the age of majority in your jurisdiction, whichever is greater);</li>
      <li>You are accessing the Platform from a jurisdiction where access to and possession of sexually explicit material is legal;</li>
      <li>You are not a public official, law-enforcement officer, or working on behalf of any agency, group, or organization engaged in the investigation or prosecution of obscenity or adult content, and you are not visiting the Platform under false pretenses;</li>
      <li>You have not falsified or misrepresented your age, identity, or location.</li>
    </ul>
    <p>We rely on date-of-birth declarations, automated age-verification signals, and, for Creators, government-issued photo ID and live selfie verification.</p>

    <h2>3. Account Registration &amp; Security</h2>
    <p>You may need to register an account to access certain features. You agree to: (a) provide truthful, complete, and current information; (b) keep your credentials confidential; (c) be solely responsible for all activity under your account; and (d) notify us immediately at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-400">{SUPPORT_EMAIL}</a> of any unauthorized use. You may not create accounts for, transfer accounts to, or share accounts with any other person. We may refuse or terminate any registration in our sole discretion.</p>

    <h2>4. Creator Accounts &amp; Identity Verification</h2>
    <p>Before uploading any content, going live, or receiving payouts, Creators must:</p>
    <ul>
      <li>Submit a valid, unexpired, government-issued photo ID (driver's license, passport, or national ID);</li>
      <li>Complete live selfie verification matching the submitted ID;</li>
      <li>Confirm date of birth and legal name;</li>
      <li>Sign the <Link to="/legal/creator-agreement" className="text-blue-400">Creator Agreement</Link>, which includes representations and warranties about content ownership, depicted-person consent, and 18 U.S.C. § 2257 record-keeping obligations;</li>
      <li>If applicable, complete tax-information collection (W-9 for U.S. persons; W-8BEN/BEN-E for non-U.S. persons) before any payout exceeding U.S. $600 in a calendar year.</li>
    </ul>
    <p><strong>Co-performers:</strong> Any third party who appears with a Creator on a live stream or in uploaded content must be independently verified by the Creator through the Platform's Co-Performer Verification flow (full legal name, date of birth, government ID, and selfie) <strong>before</strong> appearing in any content. The Platform issues a Performer ID to each verified individual that may be re-used for future collaborations. Failure to verify a co-performer is a material breach of these Terms and grounds for immediate termination.</p>

    <h2>5. Content Standards &amp; Prohibited Conduct</h2>
    <p>The following content and conduct is <strong>strictly prohibited</strong> on the Platform. Violations result in immediate content removal, account termination, reporting to law enforcement and the National Center for Missing &amp; Exploited Children (NCMEC) where applicable, and may result in civil and criminal penalties:</p>
    <ul>
      <li>Any depiction of a person under eighteen (18) years of age in a sexual, suggestive, or nude context, regardless of jurisdiction or whether real, animated, or AI-generated;</li>
      <li>Any non-consensual content, including but not limited to revenge porn, hidden-camera content, intimate images shared without consent, or any depiction obtained by coercion, deception, intoxication, or threat;</li>
      <li>Content depicting human trafficking, sexual slavery, or commercial sexual exploitation;</li>
      <li>Bestiality, necrophilia, or content involving non-consenting animals or corpses;</li>
      <li>Incest (real, simulated, or implied between blood relatives);</li>
      <li>Rape, sexual assault, or any depiction of force, coercion, or refusal that is not clearly consensual role-play with documented prior consent;</li>
      <li>Snuff, mutilation, severe injury, torture, or any sexualized violence resulting in or implying serious physical harm;</li>
      <li>Hate speech, content promoting violence against any group, or content that demeans or threatens individuals based on race, ethnicity, religion, disability, sexual orientation, or gender identity;</li>
      <li>Deepfakes, face-swaps, voice clones, or AI-generated likenesses of any real, identifiable person produced without that person's explicit, documented, and revocable written consent;</li>
      <li>Content advertising or facilitating prostitution, escort services, or in-person sexual services for compensation;</li>
      <li>Content depicting illegal drug use, the sale of controlled substances, or weapons;</li>
      <li>Personally identifying information (doxing) of any third party without consent;</li>
      <li>Content that infringes any third-party intellectual property right (copyright, trademark, right of publicity).</li>
    </ul>
    <p>The Platform employs automated and human moderation, including pre-publish review for high-risk categories and an automated content-attribution check for all aggregated third-party material. We may remove any content at any time, with or without notice, at our sole discretion.</p>

    <h2>6. Reporting Violations</h2>
    <p>If you encounter content that violates these Terms, please use the in-app report button on any piece of content or email <a href={`mailto:${ABUSE_EMAIL}`} className="text-blue-400">{ABUSE_EMAIL}</a>. Reports of child sexual abuse material (CSAM) may be submitted directly to NCMEC at <a href="https://report.cybertip.org" target="_blank" rel="noopener noreferrer" className="text-blue-400">report.cybertip.org</a>; we cooperate fully with all such investigations.</p>

    <h2>7. Intellectual Property &amp; Licenses Granted to {PLATFORM}</h2>
    <p>You retain all ownership rights to content you upload. By uploading, you grant {COMPANY} a worldwide, non-exclusive, royalty-free, sublicensable license to host, store, reproduce, transmit, display, perform, transcode, and distribute that content on and through the Platform, solely for the purpose of operating, promoting, and improving the Platform. This license terminates when you delete the content, except where retention is required by law (e.g., 18 U.S.C. § 2257 records, payment records, dispute records).</p>
    <p>You represent and warrant that you own or have all necessary rights, releases, and consents in any content you upload, including written consent from every depicted person, and that the content does not infringe any third-party right.</p>

    <h2>8. Tokens, Payments &amp; Creator Earnings</h2>
    <p>The Platform uses an in-platform token system. Tokens have no cash value outside the Platform and are non-refundable except as required by law or as set out in our <Link to="/legal/refund" className="text-blue-400">Refund Policy</Link>. Pricing, applicable fees, and the descriptor that will appear on your credit card or bank statement are disclosed at the point of purchase. We may add, modify, retire, or reprice token packages at any time with reasonable notice.</p>
    <p>Creator earnings are paid out at a minimum rate of <strong>ninety percent (90%)</strong> of net token spend attributable to the Creator (custom deals may grant a higher percentage). Payouts are subject to minimum thresholds, applicable tax withholding, and identity / banking verification. {COMPANY} retains a maximum of ten percent (10%) as a platform fee.</p>
    <p>Token purchases, subscriptions, tips, and direct payments are processed by third-party payment processors (including, where applicable, payment-card processors, crypto processors, and direct bank-transfer rails). The processor's terms apply to the underlying transaction. {COMPANY} is the merchant of record for all credit-card transactions unless otherwise disclosed.</p>

    <h2>9. Refunds, Chargebacks &amp; Cancellation</h2>
    <p>Our complete refund and cancellation rules are in the <Link to="/legal/refund" className="text-blue-400">Refund Policy</Link>. In summary:</p>
    <ul>
      <li>Token purchases are generally non-refundable, but billing errors, unauthorized transactions, and statutory refund rights (e.g., EU 14-day cooling-off period for non-digital-content sales) are honored;</li>
      <li>To request a refund, email <a href={`mailto:${BILLING_EMAIL}`} className="text-blue-400">{BILLING_EMAIL}</a> within thirty (30) days of the charge with your transaction ID and reason. We respond within two (2) business days;</li>
      <li>Filing a chargeback without first contacting us is a material breach of these Terms and may result in account termination, forfeiture of unspent tokens, and recovery of disputed amounts plus processor fees;</li>
      <li>Subscriptions (when offered) may be cancelled at any time from your account settings. Cancellation stops the next renewal; it does not refund the current term unless required by law.</li>
    </ul>

    <h2>10. Acceptable Use</h2>
    <p>You agree not to: (a) reverse engineer or scrape the Platform; (b) use the Platform to send spam or unsolicited communications; (c) interfere with the Platform's operation or security; (d) access the Platform via bots or automated tools without our written permission; (e) impersonate any person or entity; (f) use the Platform to harass, threaten, defame, or stalk any person; (g) use the Platform for any commercial purpose other than as expressly authorized.</p>

    <h2>11. Privacy &amp; Cookies</h2>
    <p>Your use of the Platform is governed by our <Link to="/legal/privacy" className="text-blue-400">Privacy Policy</Link>, which describes how we collect, use, store, and share personal information. We use cookies and similar technologies as described in the Privacy Policy. Creator verification documents (government ID, selfie, DOB) are stored encrypted at rest and are accessed only by authorized personnel for identity verification, fraud prevention, and 18 U.S.C. § 2257 compliance.</p>

    <h2>12. DMCA / Right-to-Be-Forgotten / Takedowns</h2>
    <p>{COMPANY} respects intellectual property and personal-image rights. If you believe content on the Platform infringes your copyright or depicts you without consent, submit a takedown request via our <Link to="/legal/dmca" className="text-blue-400">DMCA &amp; Takedown</Link> page or email <a href={`mailto:${DMCA_EMAIL}`} className="text-blue-400">{DMCA_EMAIL}</a>. We respond to valid notices within seventy-two (72) hours. Repeat infringers and Creators against whom multiple verified takedowns are filed will be terminated and forfeit pending payouts.</p>

    <h2>13. Geographic Restrictions</h2>
    <p>The Platform is intended for users located in jurisdictions where adult content is lawful. We may block access from jurisdictions where the Platform's content is illegal or where local law imposes additional age-verification, payment, or content-restriction obligations we cannot meet. You are responsible for compliance with your local laws.</p>

    <h2>14. Termination &amp; Suspension</h2>
    <p>We may suspend or terminate your account, with or without notice, for any breach of these Terms, the Content Policy, the Creator Agreement, or applicable law, or where we believe in good faith that continued access creates risk to other users, third parties, or {COMPANY}. Upon termination: (a) your right to use the Platform ceases immediately; (b) unspent tokens are non-refundable except as required by law or our Refund Policy; (c) pending Creator payouts that arose from compliant activity remain payable to you, subject to our chargeback / fraud recovery offset rights.</p>

    <h2>15. Disclaimers</h2>
    <p>THE PLATFORM IS PROVIDED <strong>"AS IS"</strong> AND <strong>"AS AVAILABLE"</strong>. TO THE FULLEST EXTENT PERMITTED BY LAW, {COMPANY} DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND ANY WARRANTIES ARISING FROM COURSE OF DEALING OR USAGE OF TRADE. WE DO NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.</p>

    <h2>16. Limitation of Liability</h2>
    <p>TO THE FULLEST EXTENT PERMITTED BY LAW, {COMPANY} AND ITS DIRECTORS, OFFICERS, EMPLOYEES, AGENTS, AND AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR GOODWILL, ARISING OUT OF OR RELATING TO YOUR USE OF THE PLATFORM, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. OUR TOTAL CUMULATIVE LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THE PLATFORM SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNTS YOU PAID TO {COMPANY} IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM OR (B) ONE HUNDRED U.S. DOLLARS (US$100).</p>

    <h2>17. Indemnification</h2>
    <p>You agree to defend, indemnify, and hold harmless {COMPANY}, its officers, directors, employees, agents, and affiliates, from any claim, demand, loss, damage, cost, or expense (including reasonable attorneys' fees) arising out of or relating to: (a) your content; (b) your breach of these Terms; (c) your violation of any law or third-party right; or (d) any other party's access or use of the Platform using your credentials.</p>

    <h2>18. Governing Law &amp; Dispute Resolution</h2>
    <p>These Terms are governed by the laws of the State of {GOVERNING_STATE}, U.S.A., without regard to conflict-of-laws principles. Except as provided below, any dispute arising out of or relating to these Terms or the Platform shall be brought exclusively in the state or federal courts located in Clark County, Nevada, and you consent to the personal jurisdiction of those courts.</p>
    <p><strong>Binding individual arbitration / class-action waiver.</strong> At {COMPANY}'s sole election, any dispute may be resolved by binding individual arbitration administered by the American Arbitration Association under its Consumer Arbitration Rules, seated in Las Vegas, Nevada, with one arbitrator. <strong>You and {COMPANY} each waive any right to a jury trial and any right to participate in a class, collective, or representative action.</strong> The arbitrator has authority to decide arbitrability. Nothing in this section prevents either party from seeking injunctive relief in court to protect intellectual-property or confidential-information rights.</p>

    <h2>19. Changes to These Terms</h2>
    <p>We may amend these Terms from time to time. Material changes will be posted at this URL with a new "Last updated" date and, where reasonable, notified to registered users by email or in-platform banner at least thirty (30) days before they take effect. Your continued use of the Platform after the effective date constitutes acceptance.</p>

    <h2>20. Miscellaneous</h2>
    <p>If any provision is held unenforceable, the remaining provisions remain in full force. Our failure to enforce a provision is not a waiver. You may not assign these Terms without our prior written consent; we may assign freely. These Terms (together with the documents referenced) constitute the entire agreement between you and {COMPANY} regarding the Platform.</p>

    <h2>21. Contact</h2>
    <p>
      {COMPANY}<br />
      {COMPANY_ADDRESS_LINE1}<br />
      {COMPANY_ADDRESS_LINE2}<br />
      {COMPANY_ADDRESS_COUNTRY}<br /><br />
      General support: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-400">{SUPPORT_EMAIL}</a><br />
      Legal &amp; Terms: <a href={`mailto:${LEGAL_EMAIL}`} className="text-blue-400">{LEGAL_EMAIL}</a><br />
      Billing: <a href={`mailto:${BILLING_EMAIL}`} className="text-blue-400">{BILLING_EMAIL}</a><br />
      DMCA / Takedowns: <a href={`mailto:${DMCA_EMAIL}`} className="text-blue-400">{DMCA_EMAIL}</a><br />
      Abuse / Trust &amp; Safety: <a href={`mailto:${ABUSE_EMAIL}`} className="text-blue-400">{ABUSE_EMAIL}</a>
    </p>
  </div>
);

// ==========================================
// Privacy Policy
// ==========================================
const PrivacyPolicy = () => (
  <div className="prose-custom">
    <h1>Privacy Policy</h1>
    <p className="text-slate-400 text-sm">Last updated: June 2026</p>

    <p>This Privacy Policy explains how {COMPANY} ("we", "us", "our") collects, uses, stores, shares, and protects personal information of users of the {PLATFORM} website at <a href={SITE_URL} className="text-blue-400">{SITE_URL}</a> and any related services (the "Platform"). It is incorporated into our <Link to="/legal/terms" className="text-blue-400">Terms of Service</Link>. By using the Platform, you agree to the practices described here.</p>

    <h2>1. Who We Are (Data Controller)</h2>
    <p>For the purposes of the EU General Data Protection Regulation (GDPR), the UK GDPR, and the California Consumer Privacy Act (CCPA / CPRA), the data controller is:</p>
    <p className="bg-slate-900/60 border border-white/5 rounded-xl p-4 inline-block not-prose">
      {COMPANY}<br />
      {COMPANY_ADDRESS_LINE1}<br />
      {COMPANY_ADDRESS_LINE2}<br />
      {COMPANY_ADDRESS_COUNTRY}<br /><br />
      Privacy inquiries: <a href={`mailto:${LEGAL_EMAIL}`} className="text-blue-400">{LEGAL_EMAIL}</a>
    </p>

    <h2>2. Information We Collect</h2>
    <h3>2.1 Information you provide</h3>
    <ul>
      <li><strong>Account data:</strong> display name, email address, password (stored as a salted bcrypt hash — we never see your plaintext password), date of birth, gender, pronouns, account role (viewer or creator), preferred language.</li>
      <li><strong>Profile data:</strong> avatar, bio, social links, gallery preferences.</li>
      <li><strong>Creator verification (KYC):</strong> government-issued photo ID image, live selfie biometric image, full legal name, signed Creator Agreement. Required by 18 U.S.C. § 2257 and our payment processors.</li>
      <li><strong>Co-Performer verification:</strong> for each guest a Creator brings on stream — legal name, DOB, government ID, selfie. Stored against the verifying Creator's account.</li>
      <li><strong>Payment data:</strong> we do <strong>not</strong> store full credit-card numbers. Payment processors (Epoch, NOWPayments, or other) handle card and bank data directly. We retain only the last 4 digits, brand, expiration month/year, billing country, transaction IDs, and bill descriptors. For crypto payments we store wallet addresses and on-chain transaction hashes.</li>
      <li><strong>Tax data:</strong> for Creator payouts crossing US $600 in a calendar year — W-9 (US persons) or W-8BEN/BEN-E (non-US).</li>
      <li><strong>Content you upload:</strong> images, videos, descriptions, tags, prices, and any metadata you attach.</li>
      <li><strong>Communications:</strong> in-app chat messages, private show messages, support tickets, feedback submissions, DMCA notices.</li>
    </ul>

    <h3>2.2 Information collected automatically</h3>
    <ul>
      <li><strong>Device &amp; connection:</strong> IP address (truncated for analytics, full for fraud / 2257 audit), user-agent, device type, browser, OS, language, approximate geolocation derived from IP (country and region only — never precise coordinates), referrer URL.</li>
      <li><strong>Usage:</strong> pages visited, content viewed, search queries, clicks, sessions, time-on-page, error and crash reports.</li>
      <li><strong>Cookies and similar technologies:</strong> see <a href="#cookies" className="text-blue-400">Section 6</a> below.</li>
    </ul>

    <h3>2.3 Information from third parties</h3>
    <ul>
      <li><strong>Payment processors</strong> share transaction outcomes, chargeback notices, fraud signals, and refund confirmations.</li>
      <li><strong>Identity-verification providers</strong> (if used) return match/no-match results and risk scores on submitted IDs.</li>
      <li><strong>Federated content sources</strong> (e.g., Mastodon public timelines) — we capture only the publicly-published metadata required for attribution (post URL, author handle, instance, original post ID).</li>
    </ul>

    <h2>3. How We Use Your Information</h2>
    <ul>
      <li>Provide and operate the Platform (account creation, content delivery, payments, live streaming, tokens, payouts);</li>
      <li>Verify age and identity, in compliance with 18 U.S.C. § 2257 and applicable record-keeping laws;</li>
      <li>Prevent fraud, abuse, harassment, illegal content, and unauthorized account access;</li>
      <li>Process payments, prevent chargebacks, and resolve disputes;</li>
      <li>Send transactional notifications (security alerts, billing receipts, account changes, takedown notices). These cannot be opted out of while you have an active account;</li>
      <li>Send announcements, product updates, and marketing — only when you have opted in. You can change these preferences at any time in <Link to="/settings" className="text-blue-400">Settings → Email Preferences</Link>;</li>
      <li>Comply with law, court orders, lawful government requests, and our legal obligations;</li>
      <li>Improve the Platform (aggregated, de-identified analytics).</li>
    </ul>

    <h2>4. Legal Bases (EU/UK Users)</h2>
    <p>For users in the EU, UK, EEA, or Switzerland, our legal bases under Article 6 GDPR are:</p>
    <ul>
      <li><strong>Contract:</strong> account, content delivery, payments, payouts (Art. 6(1)(b));</li>
      <li><strong>Legal obligation:</strong> KYC, 2257 record-keeping, tax forms, law-enforcement responses (Art. 6(1)(c));</li>
      <li><strong>Legitimate interests:</strong> fraud prevention, abuse moderation, product analytics, security (Art. 6(1)(f));</li>
      <li><strong>Consent:</strong> marketing emails, optional non-essential cookies, AI-content opt-in features (Art. 6(1)(a)). You may withdraw consent at any time.</li>
    </ul>
    <p>For sensitive data (e.g., the fact that you are a user of an adult platform, identity documents), our basis is your explicit consent (Art. 9(2)(a) GDPR) and/or substantial public interest under Member State law.</p>

    <h2>5. How We Share Your Information</h2>
    <p>We do <strong>not</strong> sell or rent personal information. We share only when needed:</p>
    <ul>
      <li><strong>Service providers / processors</strong>, under written contracts that bind them to our security standards: hosting (Kubernetes cloud infrastructure), database (MongoDB Atlas-compatible cluster), email delivery (Resend), live-streaming infrastructure (Cloudflare Stream), payment processors (Epoch, NOWPayments, and bank rails such as Zelle / Mexican bank transfer where applicable), identity-verification vendors, and analytics.</li>
      <li><strong>Other users</strong>, only as you choose: your public profile, content you publish, your display name in chat or as a tipper. We never expose your email or full legal name to other users.</li>
      <li><strong>Law enforcement / lawful requests:</strong> when required by valid subpoena, court order, or law, or in good faith to protect rights, property, or safety. We publish an annual transparency summary of such requests.</li>
      <li><strong>NCMEC and child-protection partners:</strong> we report suspected child sexual abuse material (CSAM) immediately and provide all required information to the National Center for Missing &amp; Exploited Children.</li>
      <li><strong>Successor entities:</strong> if {COMPANY} is acquired, merged, restructured, or sells substantially all of its assets, your information may transfer as part of that transaction. Any successor will be bound to honor this Policy or will give you advance notice.</li>
    </ul>

    <h2 id="cookies">6. Cookies &amp; Similar Technologies</h2>
    <p>We use:</p>
    <ul>
      <li><strong>Strictly necessary cookies</strong> — login session, security, CSRF protection, age-gate acknowledgement. These cannot be disabled.</li>
      <li><strong>Preference cookies</strong> — language, theme, content-filter setting.</li>
      <li><strong>Analytics</strong> — first-party usage analytics. Anonymous, aggregated.</li>
    </ul>
    <p>We do <strong>not</strong> use third-party advertising cookies, retargeting pixels, or cross-site tracking. You can manage cookies via your browser. Blocking strictly-necessary cookies will break login.</p>

    <h2>7. Data Retention</h2>
    <table className="text-sm w-full not-prose my-4">
      <thead><tr className="text-slate-400"><th className="text-left pr-4 pb-1">Data category</th><th className="text-left pb-1">Retention</th></tr></thead>
      <tbody className="text-slate-300">
        <tr><td className="pr-4 py-1">Account profile</td><td>Until you delete your account + 30-day grace period</td></tr>
        <tr><td className="pr-4 py-1">Creator KYC + Co-Performer records</td><td>7 years after the content's last public availability (2257 mandate)</td></tr>
        <tr><td className="pr-4 py-1">Payment / payout / tax records</td><td>7 years (IRS / FinCEN / accounting standard)</td></tr>
        <tr><td className="pr-4 py-1">Content you upload</td><td>Until you delete it, or until removed for policy violation</td></tr>
        <tr><td className="pr-4 py-1">Server logs / IPs</td><td>90 days, except logs flagged for fraud or law-enforcement</td></tr>
        <tr><td className="pr-4 py-1">Chat &amp; private-show messages</td><td>180 days, or longer if subject to a dispute / takedown</td></tr>
        <tr><td className="pr-4 py-1">AI-generation prompts and outputs</td><td>2 years (abuse / fraud audit)</td></tr>
        <tr><td className="pr-4 py-1">Marketing email subscription state</td><td>Until you unsubscribe; suppression list kept indefinitely so you stay unsubscribed</td></tr>
      </tbody>
    </table>

    <h2>8. International Data Transfers</h2>
    <p>{COMPANY} is based in the United States. If you access the Platform from outside the US, your information will be transferred to, stored in, and processed in the US, where data-protection laws may differ from your home country. For transfers from the EU/UK/EEA/Switzerland to the US, we rely on the Standard Contractual Clauses (SCCs) approved by the European Commission and, where applicable, the EU-US Data Privacy Framework.</p>

    <h2>9. Your Rights</h2>
    <h3>9.1 All users</h3>
    <ul>
      <li><strong>Access:</strong> get a copy of personal data we hold about you.</li>
      <li><strong>Correction:</strong> update inaccurate or incomplete data.</li>
      <li><strong>Deletion:</strong> close your account and request erasure (subject to records we are legally required to keep).</li>
      <li><strong>Portability:</strong> get your data in a structured, machine-readable format.</li>
      <li><strong>Withdraw consent:</strong> for any processing that relies on consent.</li>
      <li><strong>Opt out of marketing:</strong> via the unsubscribe link in any marketing email or in <Link to="/settings" className="text-blue-400">Settings → Email Preferences</Link>.</li>
    </ul>
    <h3>9.2 California (CCPA / CPRA)</h3>
    <p>California residents have the right to: know what categories of personal information we collect; access specific pieces collected; delete personal information; correct inaccurate personal information; opt out of "sale" or "sharing" (we do neither); limit use of sensitive personal information; and be free from retaliation for exercising these rights.</p>
    <h3>9.3 EU / UK / EEA / Switzerland (GDPR / UK GDPR)</h3>
    <p>You additionally have the right to object to processing based on legitimate interests, restrict processing, and lodge a complaint with your local data-protection authority. The UK supervisory authority is the ICO (<a href="https://ico.org.uk" className="text-blue-400" target="_blank" rel="noopener noreferrer">ico.org.uk</a>).</p>
    <h3>9.4 How to exercise these rights</h3>
    <p>Email <a href={`mailto:${LEGAL_EMAIL}`} className="text-blue-400">{LEGAL_EMAIL}</a> from the address on file with the request type ("Access", "Delete", "Portability", etc.). We respond within thirty (30) days (forty-five if extended for complexity), free of charge. We may verify your identity before acting.</p>

    <h2>10. Children</h2>
    <p>The Platform is strictly for adults 18 years of age or older. We do not knowingly collect any information from anyone under 18. If we discover that a user is under 18, we will terminate the account immediately and delete all associated data. If you believe a minor is using the Platform, email <a href={`mailto:${ABUSE_EMAIL}`} className="text-blue-400">{ABUSE_EMAIL}</a> immediately.</p>

    <h2>11. Security</h2>
    <p>We use industry-standard safeguards including TLS in transit, AES encryption at rest for ID documents and selfies, bcrypt password hashing, JWT session tokens with rotation, role-based access controls, audit logging for sensitive operations, and least-privilege production access. No system is perfectly secure; if we discover a breach affecting your information we will notify you and the appropriate authorities within the timeframes required by law (e.g., 72 hours under GDPR).</p>

    <h2>12. Account Deletion</h2>
    <p>You may delete your account at any time from <Link to="/settings" className="text-blue-400">Settings → Delete Account</Link> or by emailing <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-400">{SUPPORT_EMAIL}</a>. After deletion, your profile, content, and chat history are removed from public surfaces within 24 hours. We retain the records described in §7 only for the legally-required period (KYC, payments, tax) in an isolated system accessible solely for compliance.</p>

    <h2>13. Do Not Track</h2>
    <p>Because no industry standard for DNT exists today, our site does not respond to DNT signals. We honor browser-level opt-outs for non-essential cookies.</p>

    <h2>14. Changes to This Policy</h2>
    <p>We may amend this Policy. Material changes are announced via in-platform banner and email (where you have opted in) at least thirty (30) days before they take effect. The "Last updated" date at the top always reflects the current version.</p>

    <h2>15. Contact</h2>
    <p>Questions, rights requests, or complaints about this Policy:</p>
    <p>
      {COMPANY}<br />
      Attn: Privacy<br />
      {COMPANY_ADDRESS_LINE1}<br />
      {COMPANY_ADDRESS_LINE2}<br />
      {COMPANY_ADDRESS_COUNTRY}<br /><br />
      Email: <a href={`mailto:${LEGAL_EMAIL}`} className="text-blue-400">{LEGAL_EMAIL}</a>
    </p>
  </div>
);

// ==========================================
// Refund Policy
// ==========================================
const RefundPolicy = () => (
  <div className="prose-custom">
    <h1>Refund &amp; Cancellation Policy</h1>
    <p className="text-slate-400 text-sm">Last updated: June 2026</p>

    <p>This Refund &amp; Cancellation Policy ("Refund Policy") explains how token purchases, subscriptions, tips, and other charges on {PLATFORM} (operated by {COMPANY}) can be refunded, cancelled, or disputed. It is incorporated into our <Link to="/legal/terms" className="text-blue-400">Terms of Service</Link>.</p>

    <h2>1. The Short Version</h2>
    <ul>
      <li>Tokens, tips, and individual unlocks are <strong>generally non-refundable</strong> once delivered to your account or sent to a Creator.</li>
      <li>We <strong>do refund</strong> billing errors, unauthorized transactions, undelivered purchases, and any claim mandated by law.</li>
      <li>Subscriptions (when offered) can be cancelled any time; your access continues until the end of the current paid period.</li>
      <li>Always email <a href={`mailto:${BILLING_EMAIL}`} className="text-blue-400">{BILLING_EMAIL}</a> first — chargebacks filed without contacting us are a material breach of these Terms.</li>
    </ul>

    <h2>2. What Will Appear on Your Statement</h2>
    <p>Credit-card and bank-card charges will appear on your statement with a descriptor that includes the payment processor and the {PLATFORM} brand (e.g., <code className="bg-slate-800 px-1.5 py-0.5 rounded text-amber-300">EPOCH.COM*LUSTLINK</code> or similar). The exact descriptor in effect for your transaction is shown on the checkout page at the time of purchase and printed on your receipt email.</p>

    <h2>3. Tokens, Tips, and Unlocks</h2>
    <p>{PLATFORM} uses a token economy. When you buy tokens, the tokens are delivered to your account instantly and have no cash value outside the Platform. <strong>Once tokens are spent</strong> — to tip a Creator, unlock content, enter a private show, or pay for any other digital good — that transaction is final because the digital good has been delivered.</p>
    <p>Unspent tokens may be refunded to the original payment method (less any processor fees that are non-refundable to us) within thirty (30) days of purchase if you have not used the Platform's paid services. Use of the Platform's paid services within that window waives the refund right except where required by law.</p>

    <h2>4. Statutory Refund Rights (EU / UK Consumers)</h2>
    <p>If you are a consumer in the EU, UK, EEA, or Switzerland, you have a fourteen (14) day right of withdrawal for distance contracts. However, under Article 16(m) of the EU Consumer Rights Directive (and the equivalent UK Consumer Contracts Regulations), this right does <strong>not</strong> apply to digital content that has begun to be delivered with your express prior consent and acknowledgement that the right of withdrawal is thereby waived. At checkout, when you tick the consent box you give that consent and waive that right for tokens that have been delivered to your account. For tokens that have <strong>not yet been spent</strong>, you may still request a refund within fourteen (14) days at no charge.</p>

    <h2>5. Subscriptions &amp; Recurring Billing</h2>
    <p>Subscription-based purchases (e.g., monthly Fan Tiers, when offered) are recurring until you cancel. Each renewal charge is preceded by a reminder email at least three (3) days in advance (where required by local law, this notice is at least seven (7) days). To cancel:</p>
    <ul>
      <li>From your account: <Link to="/settings" className="text-blue-400">Settings → Subscriptions</Link> → "Cancel" next to the relevant subscription.</li>
      <li>By email: <a href={`mailto:${BILLING_EMAIL}`} className="text-blue-400">{BILLING_EMAIL}</a> from the address on file.</li>
    </ul>
    <p>Cancellation stops the next renewal. Your subscription benefits continue until the end of the term you have already paid for. Pro-rated refunds for the unused portion of the current term are not offered except where required by law.</p>

    <h2>6. Direct Payments (Zelle / Mexican Bank Transfer / Crypto)</h2>
    <p>Token purchases made via direct payment methods (Zelle, Mexican Bank Transfer, NOWPayments crypto) are reviewed and approved manually by our team. If a direct payment is rejected (for any reason — incorrect amount, KYC fail, suspected fraud, etc.) the funds are returned to the originating account within five (5) business days, less any wire / network fees imposed by the rail. Confirmed direct payments follow the same refund rules as card payments (§3 and §4).</p>

    <h2>7. Billing Errors &amp; Unauthorized Transactions</h2>
    <p>If you see a charge from us that you did not authorize, was duplicated, or is in the wrong amount, email <a href={`mailto:${BILLING_EMAIL}`} className="text-blue-400">{BILLING_EMAIL}</a> within ninety (90) days of the charge with:</p>
    <ul>
      <li>The transaction ID (from your receipt email or your account's purchase history);</li>
      <li>Date and amount of the charge;</li>
      <li>The descriptor shown on your statement;</li>
      <li>A brief explanation.</li>
    </ul>
    <p>We acknowledge requests within two (2) business days and resolve them within ten (10) business days. If approved, refunds post to the original payment method within five to ten (5–10) business days depending on the processor.</p>

    <h2>8. Chargeback Policy</h2>
    <p>We strongly prefer that you contact us first; we will almost always resolve a legitimate billing issue faster than your card issuer can. If you file a chargeback <strong>without first contacting us at <a href={`mailto:${BILLING_EMAIL}`} className="text-blue-400">{BILLING_EMAIL}</a></strong>, it is a material breach of our <Link to="/legal/terms" className="text-blue-400">Terms of Service</Link> and we may, in our sole discretion:</p>
    <ul>
      <li>Suspend or terminate the account that initiated the disputed transaction;</li>
      <li>Forfeit any unspent tokens, paid Creator gifts, or subscription time on that account;</li>
      <li>Recover from the account holder our reasonable costs of disputing the chargeback (including processor representment fees and reasonable attorneys' fees);</li>
      <li>Add the email address, IP, and payment fingerprint to our internal block list to prevent future signups using the same identifiers.</li>
    </ul>
    <p>We honor chargebacks initiated through valid statutory consumer-protection regimes; we contest only chargebacks that lack a documented basis.</p>

    <h2>9. Creator-Side Effects of Refunds &amp; Chargebacks</h2>
    <p>When tokens that were already credited to a Creator are refunded or successfully charged back to the viewer, the Creator's available earnings balance is reduced by the corresponding amount (the Creator's 90% share plus any platform-fee accounting), per the Creator Agreement. This is automatic and applies to all refund channels. If a Creator's earnings balance is insufficient at the time of a refund/chargeback, the deficit is recovered from future earnings before any further payout is released.</p>

    <h2>10. Things We Cannot Refund</h2>
    <ul>
      <li><strong>Tips already received by a Creator</strong> who has already withdrawn the corresponding earnings — once a Creator has been paid out, those funds are gone from our system. We will still attempt good-faith recovery in fraud / unauthorized-charge cases.</li>
      <li><strong>Charges resulting from violations of our Terms</strong> — accounts terminated for policy violation forfeit unspent tokens.</li>
    </ul>

    <h2>11. Disputes</h2>
    <p>If you disagree with how we resolved a refund request, you may escalate by emailing <a href={`mailto:${LEGAL_EMAIL}`} className="text-blue-400">{LEGAL_EMAIL}</a>. The dispute-resolution provisions of our <Link to="/legal/terms" className="text-blue-400">Terms of Service</Link> (governing law, jurisdiction, and arbitration / class-action waiver) apply to refund-related disputes.</p>

    <h2>12. Contact</h2>
    <p>
      Billing &amp; refund questions: <a href={`mailto:${BILLING_EMAIL}`} className="text-blue-400">{BILLING_EMAIL}</a><br />
      Legal escalation: <a href={`mailto:${LEGAL_EMAIL}`} className="text-blue-400">{LEGAL_EMAIL}</a><br /><br />
      Mailing address:<br />
      {COMPANY}<br />
      Attn: Billing<br />
      {COMPANY_ADDRESS_LINE1}<br />
      {COMPANY_ADDRESS_LINE2}<br />
      {COMPANY_ADDRESS_COUNTRY}
    </p>
  </div>
);

// ==========================================
// Content Policy
// ==========================================
const ContentPolicy = () => (
  <div className="prose-custom">
    <h1>Content Policy</h1>
    <p className="text-slate-400 text-sm">Last updated: April 2026</p>

    <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 my-6">
      <div className="flex items-start gap-3">
        <Warning weight="fill" className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <p className="text-blue-200 text-sm font-medium">Violation of this policy will result in immediate content removal, account suspension, and potential legal action.</p>
      </div>
    </div>

    <h2>Prohibited Content</h2>
    <p>The following content is strictly prohibited on {PLATFORM}:</p>

    <h3>1. Minors / Child Sexual Abuse Material (CSAM)</h3>
    <p>Any content depicting, suggesting, or referencing minors (anyone under 18) in a sexual context is absolutely prohibited. This includes real, drawn, AI-generated, or fictional depictions. Violations are reported to NCMEC and law enforcement.</p>

    <h3>2. Non-Consensual Content</h3>
    <p>All depicted persons must have given explicit, documented consent. This includes:</p>
    <ul>
      <li>Revenge porn or intimate images shared without consent</li>
      <li>Hidden camera / voyeur content without participant consent</li>
      <li>Content depicting sexual assault or rape (real or simulated without clear disclaimer)</li>
    </ul>

    <h3>3. Deepfakes & Non-Consensual AI Depictions</h3>
    <p>AI-generated or manipulated content depicting real persons without their explicit written consent is strictly prohibited. This includes:</p>
    <ul>
      <li>Deepfake pornography</li>
      <li>Celebrity look-alike content designed to impersonate real persons</li>
      <li>Face-swapped or body-morphed content using real persons' likenesses</li>
    </ul>

    <h3>4. Extreme Content</h3>
    <ul>
      <li>Bestiality or zoophilia</li>
      <li>Incest (real or marketed as such)</li>
      <li>Extreme violence, gore, or content depicting death</li>
      <li>Scat, vomit, or extreme bodily functions used in sexual context</li>
      <li>Content promoting self-harm or suicide</li>
    </ul>

    <h3>5. Illegal Content</h3>
    <ul>
      <li>Content that violates any applicable local, state, national, or international law</li>
      <li>Content promoting human trafficking or sexual exploitation</li>
      <li>Content promoting illegal drug use in sexual contexts</li>
      <li>Copyright-infringing content (without proper licensing)</li>
    </ul>

    <h2>Content Requirements</h2>
    <h3>For Creators Uploading Content:</h3>
    <ul>
      <li>All depicted persons must be 18+ with documented proof of age</li>
      <li>Written consent (model release) required for all depicted persons</li>
      <li>Creators must maintain 2257-compliant records for all content</li>
      <li>Content must be accurately categorized and tagged</li>
    </ul>

    <h3>For AI-Generated Content:</h3>
    <ul>
      <li>Must not depict real persons without explicit consent</li>
      <li>Must not be used to create CSAM or minor-adjacent content</li>
      <li>Must be labeled as AI-generated</li>
      <li>Prompts attempting to generate prohibited content are logged and may result in account termination</li>
    </ul>

    <h2>Moderation Process</h2>
    <p>{PLATFORM} employs the following moderation measures:</p>
    <ul>
      <li><strong>Automated Scanning:</strong> All uploaded content is scanned for prohibited material</li>
      <li><strong>Manual Review:</strong> Admin team reviews flagged content and user reports within 24 hours</li>
      <li><strong>Community Reporting:</strong> Users can report content violations via the built-in reporting system</li>
      <li><strong>Strike System:</strong> First violation = warning, Second = 7-day suspension, Third = permanent ban</li>
      <li><strong>AI Prompt Monitoring:</strong> AI generation prompts are logged and audited for policy compliance</li>
    </ul>

    <h2>Reporting Violations</h2>
    <p>To report content that violates this policy, use the in-app report button on any content, or email <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-400">{SUPPORT_EMAIL}</a>.</p>
  </div>
);

// ==========================================
// 2257 Compliance
// ==========================================
const Compliance2257 = () => (
  <div className="prose-custom">
    <h1>18 U.S.C. § 2257 Record-Keeping Requirements Compliance Statement</h1>
    <p className="text-slate-400 text-sm">Last updated: June 2026</p>

    <p>In compliance with the Federal Labeling and Record-Keeping Law (commonly known as 18 U.S.C. § 2257), all models, actors, actresses, and other persons who appear in any visual depiction of actual or simulated sexually explicit conduct appearing on, or otherwise contained in, this Website were over the age of eighteen (18) years at the time the visual depiction was created.</p>

    <p>This Website is operated by {COMPANY} (the parent company of the {PLATFORM} division and other media properties).</p>

    <h2>Custodian of Records</h2>
    <p>The original records required pursuant to 18 U.S.C. § 2257 and 28 C.F.R. Part 75 for all content produced by {COMPANY} (and the {PLATFORM} division) are kept by the following Custodian of Records, at the following physical street address:</p>
    <p className="bg-slate-900/60 border border-white/5 rounded-xl p-4 inline-block not-prose">
      <strong>Custodian of Records</strong><br />
      {COMPANY}<br />
      {COMPANY_ADDRESS_LINE1}<br />
      {COMPANY_ADDRESS_LINE2}<br />
      {COMPANY_ADDRESS_COUNTRY}<br /><br />
      Records inquiries: <a href={`mailto:${LEGAL_EMAIL}`} className="text-blue-400">{LEGAL_EMAIL}</a>
    </p>

    <p>Records are made available for inspection by the Attorney General of the United States and any other lawful authority during regular business hours at the physical address listed above, subject to advance written request as required by 28 C.F.R. § 75.5.</p>

    <h2>User-Generated Content</h2>
    <p>For user-uploaded content, {PLATFORM} operates as a third-party service provider under 18 U.S.C. § 2257A(h)(1). The primary record-keeping obligation rests with the producer (Creator). As a condition of uploading content, every Creator must:</p>
    <ul>
      <li>Complete identity verification (unexpired government-issued photo ID + live selfie biometric match);</li>
      <li>Confirm date of birth and legal name;</li>
      <li>Sign the <Link to="/legal/creator-agreement" className="text-blue-400">Creator Agreement</Link>, in which the Creator represents and warrants that all depicted persons were at least eighteen (18) years of age at the time of creation and that the Creator maintains 2257-compliant records for each depiction;</li>
      <li>Verify any co-performer through the Platform's Co-Performer Verification flow (full legal name, DOB, government ID, selfie) <strong>before</strong> the co-performer appears in any uploaded or live content. The Platform issues and persists a unique Performer ID for each verified individual;</li>
      <li>Provide age and identity documentation for any depicted person upon request from {COMPANY} or any lawful authority.</li>
    </ul>

    <h2>Platform-Held Records</h2>
    <p>{COMPANY} retains, in encrypted form, copies of:</p>
    <ul>
      <li>Creator-submitted government IDs, selfies, and signed Creator Agreements;</li>
      <li>Co-Performer verification packages (legal name, DOB, ID image, selfie image, association with the verifying Creator);</li>
      <li>Stream and content metadata (uploader identity, timestamp, source IP at upload, the IDs of all Performers associated with the content).</li>
    </ul>
    <p>These records are retained for the longer of (a) seven (7) years following the last public availability of the associated content or (b) such longer period as required by applicable law.</p>

    <h2>AI-Generated Content</h2>
    <p>AI-generated visual content hosted on this Website does not depict actual human beings. Accordingly, the 2257 record-keeping requirements do not apply to purely AI-generated imagery. As a matter of internal policy, however, {PLATFORM} retains the originating user account, prompt, model identifier, timestamp, and resulting media hash for every AI-generated image for fraud-prevention and abuse-investigation purposes.</p>

    <h2>Third-Party / Aggregated Content</h2>
    <p>Content discovered and surfaced from third-party platforms (where lawful) is linked to its original source post on the third-party platform, including the original author's handle and profile URL. {PLATFORM} does not produce or re-upload such content; record-keeping obligations remain with the original producer and host. Items lacking full source attribution are removed by an automated nightly attribution-validation sweep. Takedown requests for any aggregated item may be submitted via our <Link to="/legal/dmca" className="text-blue-400">DMCA &amp; Takedown</Link> page.</p>

    <h2>Exemption</h2>
    <p>Visual depictions of mere nudity (without sexually explicit conduct), as defined at 18 U.S.C. § 2256(2)(A), are not subject to 2257 record-keeping requirements. To the extent any depiction on this Website constitutes only "lascivious exhibition" insufficient to trigger record-keeping obligations, no statement herein should be construed as an admission that 2257 applies.</p>
  </div>
);

// ==========================================
// DMCA Takedown Form
// ==========================================
const DMCAPage = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    content_url: '',
    description: '',
    relationship: 'depicted_person',
    sworn_statement: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.sworn_statement) {
      toast.error('You must check the sworn statement checkbox');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/legal/dmca/submit`, formData);
      setSubmitted(true);
      toast.success('DMCA request submitted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-16">
        <CheckCircle weight="fill" className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-white mb-2">Request Submitted</h2>
        <p className="text-slate-400 mb-6">We will review your takedown request within 48 hours and notify you at the email address provided.</p>
        <Link to="/" className="text-blue-400 hover:text-blue-300">Return to Home</Link>
      </div>
    );
  }

  return (
    <div className="prose-custom">
      <h1>DMCA Takedown Request</h1>
      <p className="text-slate-400 mb-6">If you believe content on {PLATFORM} infringes your copyright or depicts you without consent, submit a takedown request below.</p>

      <form onSubmit={handleSubmit} className="space-y-5 max-w-xl">
        <div>
          <Label>Full Legal Name *</Label>
          <Input
            value={formData.full_name}
            onChange={(e) => setFormData({...formData, full_name: e.target.value})}
            required
            className="bg-slate-800 border-white/10 mt-1"
            data-testid="dmca-name"
          />
        </div>

        <div>
          <Label>Email Address *</Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            required
            className="bg-slate-800 border-white/10 mt-1"
            data-testid="dmca-email"
          />
        </div>

        <div>
          <Label>URL of Infringing Content *</Label>
          <Input
            value={formData.content_url}
            onChange={(e) => setFormData({...formData, content_url: e.target.value})}
            placeholder="https://lust-link.net/content/..."
            required
            className="bg-slate-800 border-white/10 mt-1"
            data-testid="dmca-url"
          />
        </div>

        <div>
          <Label>Your Relationship to the Content *</Label>
          <select
            value={formData.relationship}
            onChange={(e) => setFormData({...formData, relationship: e.target.value})}
            className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-2 mt-1 text-white"
            data-testid="dmca-relationship"
          >
            <option value="depicted_person">I am depicted in this content</option>
            <option value="copyright_holder">I am the copyright holder</option>
            <option value="authorized_agent">I am an authorized agent of the rights holder</option>
          </select>
        </div>

        <div>
          <Label>Description *</Label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            placeholder="Please describe the content and why it should be removed..."
            required
            rows={4}
            className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 mt-1 text-white resize-none"
            data-testid="dmca-description"
          />
        </div>

        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.sworn_statement}
              onChange={(e) => setFormData({...formData, sworn_statement: e.target.checked})}
              className="mt-1 w-4 h-4 accent-blue-500"
              data-testid="dmca-sworn"
            />
            <span className="text-sm text-amber-200">
              I swear, under penalty of perjury, that the information in this notification is accurate, and that I am the rights holder or am authorized to act on behalf of the rights holder for the content described above.
            </span>
          </label>
        </div>

        <button
          type="submit"
          disabled={submitting || !formData.full_name || !formData.email || !formData.content_url || !formData.description}
          className="px-8 py-3 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600 transition-all disabled:opacity-50"
          data-testid="dmca-submit"
        >
          {submitting ? 'Submitting...' : 'Submit Takedown Request'}
        </button>
      </form>
    </div>
  );
};

// ==========================================
// Creator Agreement (for display)
// ==========================================
const CreatorAgreement = () => (
  <div className="prose-custom">
    <h1>Creator Agreement</h1>
    <p className="text-slate-400 text-sm">Version 1.0 — April 2026</p>

    <p>This Creator Agreement ("Agreement") is between {COMPANY} d/b/a {PLATFORM} ("Platform") and you ("Creator"). By signing this Agreement, you agree to the following terms:</p>

    <h2>1. Eligibility & Identity Verification</h2>
    <ul>
      <li>You certify that you are at least 18 years of age.</li>
      <li>You have provided a valid government-issued photo ID and selfie verification.</li>
      <li>The information you provided during registration and verification is true, accurate, and complete.</li>
    </ul>

    <h2>2. Content Ownership & Licensing</h2>
    <ul>
      <li>You retain full ownership of original content you upload to {PLATFORM}.</li>
      <li>You grant {PLATFORM} a non-exclusive, worldwide, royalty-free license to host, display, distribute, and promote your content on the platform.</li>
      <li>You represent that you have the right to upload and license all content you submit.</li>
    </ul>

    <h2>3. Depicted Persons & Consent</h2>
    <ul>
      <li>All persons depicted in your content are at least 18 years of age.</li>
      <li>You have obtained and maintain written consent (model release) from all persons depicted in your content.</li>
      <li>You will provide proof of age and consent documentation upon request by {PLATFORM} or any authorized authority.</li>
    </ul>

    <h2>4. 18 U.S.C. 2257 Compliance</h2>
    <ul>
      <li>You agree to maintain records as required by 18 U.S.C. 2257 and 28 C.F.R. Part 75 for all content you produce.</li>
      <li>These records include government-issued photo ID copies and proof of age for all depicted persons.</li>
      <li>You will make these records available for inspection upon lawful request.</li>
    </ul>

    <h2>5. Prohibited Content</h2>
    <p>You agree not to upload content that violates the <Link to="/legal/content-policy" className="text-blue-400">Content Policy</Link>, including but not limited to:</p>
    <ul>
      <li>Content depicting minors</li>
      <li>Non-consensual content</li>
      <li>Deepfakes or non-consensual AI depictions of real persons</li>
      <li>Content depicting bestiality, extreme violence, or other prohibited categories</li>
    </ul>

    <h2>6. Revenue Split</h2>
    <ul>
      <li>Creators receive a minimum of 90% of all revenue from subscriptions, tips, and content purchases (custom deals may grant higher percentages).</li>
      <li>{PLATFORM} retains a maximum of 10% as a platform fee.</li>
      <li>Payouts are processed according to the payment schedule and minimum thresholds.</li>
    </ul>

    <h2>7. Content Removal & Takedowns</h2>
    <ul>
      <li>{PLATFORM} may remove content that violates policies or in response to valid DMCA takedown requests.</li>
      <li>Creators will be notified of content removals and may appeal through the moderation system.</li>
      <li>Repeated violations will result in account suspension or termination.</li>
    </ul>

    <h2>8. Termination</h2>
    <p>Either party may terminate this Agreement at any time. Upon termination, your content will be removed from the platform within 30 days. Outstanding earnings will be paid out according to the normal payment schedule.</p>

    <h2>9. Indemnification</h2>
    <p>You agree to indemnify and hold harmless {COMPANY} and {PLATFORM} from any claims, damages, or liabilities arising from content you upload or your breach of this Agreement.</p>
  </div>
);

// ==========================================
// Main Legal Page Router
// ==========================================
const PAGES = {
  'terms': { component: TermsOfService, title: 'Terms of Service', icon: Scales },
  'privacy': { component: PrivacyPolicy, title: 'Privacy Policy', icon: ShieldCheck },
  'refund': { component: RefundPolicy, title: 'Refund & Cancellation Policy', icon: FileText },
  'content-policy': { component: ContentPolicy, title: 'Content Policy', icon: ShieldCheck },
  '2257': { component: Compliance2257, title: '2257 Compliance', icon: FileText },
  'dmca': { component: DMCAPage, title: 'DMCA Takedown', icon: Gavel },
  'creator-agreement': { component: CreatorAgreement, title: 'Creator Agreement', icon: FileText },
};

const LegalPage = () => {
  const { page } = useParams();
  const pageInfo = PAGES[page];

  // Index page if no specific page
  if (!page || !pageInfo) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="font-heading text-3xl font-bold text-white mb-2">Legal</h1>
        <p className="text-slate-400 mb-8">Platform policies and legal documents</p>
        <div className="grid gap-4">
          {Object.entries(PAGES).map(([slug, info]) => (
            <Link
              key={slug}
              to={`/legal/${slug}`}
              className="flex items-center gap-4 p-5 bg-slate-900/50 rounded-2xl border border-white/5 hover:border-white/10 transition-all group"
              data-testid={`legal-link-${slug}`}
            >
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <info.icon className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold group-hover:text-blue-400 transition-colors">{info.title}</h3>
              </div>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  const PageComponent = pageInfo.component;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 pb-24">
      <Link to="/legal" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" />
        All Legal Documents
      </Link>
      <PageComponent />
    </div>
  );
};

export default LegalPage;
